import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { IntelligenceGroupBy, UnitIntelligenceService } from 'src/app/services/unit-intelligence.service';
import { UtilsService } from 'src/app/services/utils.service';

const ROUTE_INSIGHT_LABEL: Record<IntelligenceGroupBy, string> = {
    unit: 'Ruta más utilizada',
    driver: 'Ruta más recorrida',
    client: 'Ruta más costosa',
};

const RELATED_TYPE_LABEL: Record<IntelligenceGroupBy, string> = {
    unit: 'Unidad más utilizada',
    driver: 'Operador más frecuente',
    client: 'Cliente más frecuente',
};

const STATUS_ICON: Record<string, string> = {
    'Finalizado': 'assets/img/icons/intel-timeline-check.svg',
    'En ruta': 'assets/img/icons/intel-timeline-en-ruta.svg',
    'Con retraso': 'assets/img/icons/intel-timeline-con-retraso.svg',
    'En progreso': 'assets/img/icons/intel-timeline-en-progreso.svg',
};

const STATUS_CLASS: Record<string, string> = {
    'Finalizado': 'status--finalizado',
    'En ruta': 'status--en-ruta',
    'Con retraso': 'status--con-retraso',
    'En progreso': 'status--en-progreso',
};

@Component({
    selector: 'app-intelligence-history',
    templateUrl: './intelligence-history.component.html',
    styleUrls: ['./intelligence-history.component.css']
})
export class IntelligenceHistoryComponent implements OnInit {

    readonly Math = Math;

    groupBy: IntelligenceGroupBy = 'unit';
    entityId = 0;
    routeInsightLabel = ROUTE_INSIGHT_LABEL.unit;

    filterFrom = '';
    filterTo = '';

    data: any = null;
    loading = false;

    currentPage = 1;
    perPage = 10;
    expandedServiceId: number | null = null;
    expandedDetail: any = null;
    detailLoading = false;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private svc: UnitIntelligenceService,
        private spinner: NgxSpinnerService,
        private utils: UtilsService
    ) { }

    ngOnInit(): void {
        const today = new Date();
        const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        this.filterFrom = firstOfMonth.toISOString().slice(0, 10);
        this.filterTo = today.toISOString().slice(0, 10);

        this.route.params.subscribe(params => {
            const groupBy = params['groupBy'] as IntelligenceGroupBy;
            this.groupBy = ROUTE_INSIGHT_LABEL[groupBy] ? groupBy : 'unit';
            this.routeInsightLabel = ROUTE_INSIGHT_LABEL[this.groupBy];
            this.entityId = Number(params['id']);
            this.currentPage = 1;
            this.expandedServiceId = null;
            this.fetch();
        });
    }

    fetch(): void {
        this.loading = true;
        this.spinner.show();
        const params: any = { from: this.filterFrom, to: this.filterTo, page: this.currentPage, per_page: this.perPage };

        this.svc.getHistory(this.groupBy, this.entityId, params).subscribe({
            next: (resp: any) => {
                this.data = resp;
                this.loading = false;
                this.spinner.hide();
                const firstRow = resp?.history?.data?.[0];
                if (this.currentPage === 1 && firstRow?.detail) {
                    this.expandedServiceId = firstRow.service_id;
                    this.expandedDetail = firstRow.detail;
                }
            },
            error: () => {
                this.loading = false;
                this.spinner.hide();
                this.utils.alertSwal({ title: 'Error', text: 'No se pudo obtener el historial operativo', icon: 'error' });
            }
        });
    }

    onFilterChanged(): void {
        this.currentPage = 1;
        this.fetch();
    }

    goToPage(page: number): void {
        if (page < 1 || (this.data?.history?.pagination?.last_page && page > this.data.history.pagination.last_page)) return;
        this.currentPage = page;
        this.expandedServiceId = null;
        this.fetch();
    }

    get pageNumbers(): number[] {
        const lastPage = this.data?.history?.pagination?.last_page || 1;
        const windowSize = 3;
        const end = Math.min(lastPage, Math.max(1, this.currentPage - 1) + windowSize - 1);
        const start = Math.max(1, end - windowSize + 1);
        const pages: number[] = [];
        for (let i = start; i <= end; i++) pages.push(i);
        return pages;
    }

    toggleRow(row: any): void {
        if (this.expandedServiceId === row.service_id) {
            this.expandedServiceId = null;
            this.expandedDetail = null;
            return;
        }
        if (row.detail) {
            this.expandedServiceId = row.service_id;
            this.expandedDetail = row.detail;
            return;
        }
        this.expandedServiceId = row.service_id;
        this.expandedDetail = null;
        this.detailLoading = true;
        this.svc.getServiceDetail(this.groupBy, this.entityId, row.service_id).subscribe({
            next: (resp: any) => {
                this.expandedDetail = resp;
                this.detailLoading = false;
            },
            error: () => {
                this.detailLoading = false;
            }
        });
    }

    goBack(): void {
        this.router.navigate(['/unit/intelligence-center/ranking', this.groupBy]);
    }

    relatedLabel(type: IntelligenceGroupBy): string {
        return RELATED_TYPE_LABEL[type] || '';
    }

    timelineIcon(status: string): string {
        return STATUS_ICON[status] || STATUS_ICON['En ruta'];
    }

    statusClass(status: string): string {
        return STATUS_CLASS[status] || 'status--en-ruta';
    }

    costPerKmClass(value: number | null): string {
        if (value === null || value === undefined) return '';
        if (value <= 5) return 'cell-cost--low';
        if (value <= 8) return 'cell-cost--mid';
        return 'cell-cost--high';
    }

    formatCurrency(value: number | null | undefined): string {
        if (value === null || value === undefined) return '—';
        return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(value);
    }

    formatKm(value: number | null | undefined): string {
        if (value === null || value === undefined) return '—';
        return `${new Intl.NumberFormat('es-MX').format(value)} km`;
    }

    formatDate(value: string): string {
        if (!value) return '—';
        const [yyyy, mm, dd] = value.split('T')[0].split('-');
        return `${dd}/${mm}/${yyyy}`;
    }
}
